import { api } from "./client";

// Типы 1:1 к backend/src/seller_helper/orders/schemas_v2.py (снейк-кейс).

export interface ArticleAttrs {
  article: string;
  product_type: string;
  characteristic: string;
  volume_ml: number | null;
  merge_product: string;
  semi_finished: string;
  comment: string;
}

export interface ArticleAttrsUpsert {
  article: string;
  product_type?: string;
  characteristic?: string;
  volume_ml?: number | null;
  merge_product?: string;
  semi_finished?: string;
  comment?: string;
}

export interface ProductType {
  id: number;
  name: string;
  sort_order: number;
}

export interface Characteristic {
  id: number;
  name: string;
  color: string;
  sort_order: number;
}

export interface WarehouseClusterRule {
  id: number;
  mp: "all" | "ozon" | "wb" | "ym";
  name: string;
  color: string;
  patterns: string;
  sort_order: number;
}

export interface PlanChannel {
  id: number;
  code: string;
  name: string;
  kind: "mp" | "wholesale";
  sort_order: number;
}

export interface OrdersSetting {
  key: string;
  value: unknown;
}

export interface RowHighlight {
  article: string;
  color: string;
}

export interface LogItem {
  id: number;
  ts: string;
  username: string;
  action: string;
  details: string;
}

export interface LogList {
  total: number;
  items: LogItem[];
}

// ── refs ──
export async function listAttrs(): Promise<ArticleAttrs[]> {
  const { data } = await api.get<ArticleAttrs[]>("/orders/refs/attrs");
  return data;
}

export async function upsertAttrs(body: ArticleAttrsUpsert): Promise<ArticleAttrs> {
  const { data } = await api.put<ArticleAttrs>("/orders/refs/attrs", body);
  return data;
}

export interface AttrsBulkRow {
  article: string;
  product_type: string;
  characteristic: string;
  volume_ml: number | null;
  merge_product: string;
  semi_finished: string;
}

export async function bulkAttrs(rows: AttrsBulkRow[]): Promise<{ imported: number }> {
  const { data } = await api.post<{ imported: number }>("/orders/refs/attrs/bulk", { rows });
  return data;
}

export async function listProductTypes(): Promise<ProductType[]> {
  const { data } = await api.get<ProductType[]>("/orders/refs/product-types");
  return data;
}

export async function createProductType(name: string): Promise<ProductType> {
  const { data } = await api.post<ProductType>("/orders/refs/product-types", { name });
  return data;
}

export async function updateProductType(
  id: number,
  body: { name?: string; sort_order?: number },
): Promise<ProductType> {
  const { data } = await api.put<ProductType>(`/orders/refs/product-types/${id}`, body);
  return data;
}

export async function deleteProductType(id: number): Promise<void> {
  await api.delete(`/orders/refs/product-types/${id}`);
}

export async function listCharacteristics(): Promise<Characteristic[]> {
  const { data } = await api.get<Characteristic[]>("/orders/refs/characteristics");
  return data;
}

export async function createCharacteristic(body: {
  name: string;
  color?: string;
}): Promise<Characteristic> {
  const { data } = await api.post<Characteristic>("/orders/refs/characteristics", body);
  return data;
}

export async function updateCharacteristic(
  id: number,
  body: { name?: string; color?: string; sort_order?: number },
): Promise<Characteristic> {
  const { data } = await api.put<Characteristic>(`/orders/refs/characteristics/${id}`, body);
  return data;
}

export async function deleteCharacteristic(id: number): Promise<void> {
  await api.delete(`/orders/refs/characteristics/${id}`);
}

export async function listClusters(): Promise<WarehouseClusterRule[]> {
  const { data } = await api.get<WarehouseClusterRule[]>("/orders/refs/clusters");
  return data;
}

export async function createCluster(body: {
  mp: string;
  name: string;
  color?: string;
  patterns: string;
}): Promise<WarehouseClusterRule> {
  const { data } = await api.post<WarehouseClusterRule>("/orders/refs/clusters", body);
  return data;
}

export async function updateCluster(
  id: number,
  body: Partial<Omit<WarehouseClusterRule, "id">>,
): Promise<WarehouseClusterRule> {
  const { data } = await api.put<WarehouseClusterRule>(`/orders/refs/clusters/${id}`, body);
  return data;
}

export async function deleteCluster(id: number): Promise<void> {
  await api.delete(`/orders/refs/clusters/${id}`);
}

export async function listChannels(): Promise<PlanChannel[]> {
  const { data } = await api.get<PlanChannel[]>("/orders/refs/channels");
  return data;
}

export async function createChannel(body: { name: string; kind: "mp" | "wholesale" }): Promise<PlanChannel> {
  const { data } = await api.post<PlanChannel>("/orders/refs/channels", body);
  return data;
}

export async function updateChannel(
  id: number,
  body: { name?: string; kind?: "mp" | "wholesale"; sort_order?: number },
): Promise<PlanChannel> {
  const { data } = await api.put<PlanChannel>(`/orders/refs/channels/${id}`, body);
  return data;
}

export async function deleteChannel(id: number): Promise<void> {
  await api.delete(`/orders/refs/channels/${id}`);
}

export async function listSettings(): Promise<OrdersSetting[]> {
  const { data } = await api.get<OrdersSetting[]>("/orders/refs/settings");
  return data;
}

export async function updateSetting(key: string, value: unknown): Promise<OrdersSetting> {
  const { data } = await api.put<OrdersSetting>(`/orders/refs/settings/${key}`, { value });
  return data;
}

export async function listHighlights(): Promise<RowHighlight[]> {
  const { data } = await api.get<RowHighlight[]>("/orders/refs/highlights");
  return data;
}

export async function bulkHighlights(
  articles: string[],
  color: string | null,
): Promise<{ updated: number }> {
  const { data } = await api.post<{ updated: number }>("/orders/refs/highlights/bulk", {
    articles,
    color,
  });
  return data;
}

// ── прогноз продаж ──
export interface SalesPlanCell {
  article: string;
  channel_code: string;
  month: number;
  qty: number;
}

export async function listSalesPlan(year: number): Promise<SalesPlanCell[]> {
  const { data } = await api.get<SalesPlanCell[]>("/orders/sales-plan", { params: { year } });
  return data;
}

export async function upsertSalesPlanCell(body: {
  year: number;
  article: string;
  channel_code: string;
  month: number;
  qty: number;
}): Promise<{ qty: number }> {
  const { data } = await api.put<{ qty: number }>("/orders/sales-plan/cell", body);
  return data;
}

export async function bulkSalesPlan(
  year: number,
  entries: { article: string; channel_code: string; months: Record<number, number> }[],
): Promise<{ written: number }> {
  const { data } = await api.post<{ written: number }>("/orders/sales-plan/bulk", { year, entries });
  return data;
}

export async function clearSalesPlan(year: number): Promise<{ deleted: number }> {
  const { data } = await api.post<{ deleted: number }>("/orders/sales-plan/clear", { year });
  return data;
}

// ── план производства ──
export interface ProductionCell {
  article: string;
  week_key: string;
  channel_code: string;
  qty: number;
}

export interface ProductionProduced {
  article: string;
  week_key: string;
  produced_at: string;
  produced_by: string;
}

export interface ProductionComment {
  article: string;
  month: string;
  channel_code: string;
  text: string;
}

export interface ProductionChange {
  article: string;
  week_key: string;
  edit_week: number;
  old_value: number;
  new_value: number;
  ts: string;
  username: string;
}

export interface MergeConfirmation {
  article: string;
  week_key: string;
  username: string;
  matches: unknown[];
}

export interface ProductionPayload {
  year: number;
  cells: ProductionCell[];
  produced: ProductionProduced[];
  comments: ProductionComment[];
  changes: ProductionChange[];
  confirmations: MergeConfirmation[];
}

export async function getProduction(year: number): Promise<ProductionPayload> {
  const { data } = await api.get<ProductionPayload>("/orders/production", { params: { year } });
  return data;
}

export async function putProductionCell(body: {
  year: number;
  article: string;
  week_key: string;
  channel_code: string;
  qty: number;
}): Promise<{ total: number }> {
  const { data } = await api.put<{ total: number }>("/orders/production/cell", body);
  return data;
}

export async function quickPlan(body: {
  year: number;
  article: string;
  week_key: string;
  channel_code: string;
  qty: number;
  close_control_mp?: string | null;
}): Promise<{ added: number; control_closed: boolean }> {
  const { data } = await api.post<{ added: number; control_closed: boolean }>(
    "/orders/production/quick-plan",
    body,
  );
  return data;
}

export async function setProduced(body: {
  year: number;
  article: string;
  week_key: string;
  produced: boolean;
}): Promise<void> {
  await api.post("/orders/production/produced", body);
}

export async function putProductionComment(body: {
  year: number;
  article: string;
  month: string;
  channel_code: string;
  text: string;
}): Promise<void> {
  await api.put("/orders/production/comment", body);
}

export async function bulkProduction(
  year: number,
  entries: { article: string; weeks: Record<string, number>; comments: Record<string, string> }[],
): Promise<{ written: number }> {
  const { data } = await api.post<{ written: number }>("/orders/production/bulk", { year, entries });
  return data;
}

export async function clearProduction(year: number): Promise<{ deleted: number }> {
  const { data } = await api.post<{ deleted: number }>("/orders/production/clear", { year });
  return data;
}

export async function clearProductionChange(body: {
  year: number;
  article: string;
  week_key: string;
}): Promise<void> {
  await api.post("/orders/production/change/clear", body);
}

export async function confirmMerge(body: {
  year: number;
  article: string;
  week_key: string;
  matches: unknown[];
}): Promise<void> {
  await api.put("/orders/production/merge-confirmation", body);
}

export async function unconfirmMerge(body: {
  year: number;
  article: string;
  week_key: string;
}): Promise<void> {
  await api.post("/orders/production/merge-confirmation/remove", body);
}

export async function mergeMoves(
  year: number,
  moves: { article: string; source_week: string; target_week: string }[],
): Promise<{ moved: number }> {
  const { data } = await api.post<{ moved: number }>("/orders/production/merge-moves", {
    year,
    moves,
  });
  return data;
}

// ── контроль ──
export interface ControlItem {
  mp: "wb" | "ozon" | "ym";
  article: string;
  name: string;
  brand: string;
  code_1c: string;
  id_1c: string;
  created_by: string;
  created_at: string;
}

export async function listControl(): Promise<ControlItem[]> {
  const { data } = await api.get<ControlItem[]>("/orders/control");
  return data;
}

export async function addControl(body: {
  mp: string;
  article: string;
  name?: string;
  brand?: string;
  code_1c?: string;
  id_1c?: string;
}): Promise<ControlItem> {
  const { data } = await api.put<ControlItem>("/orders/control", body);
  return data;
}

export async function removeControl(body: {
  mp: string;
  article: string;
  reason: "uncheck" | "ok" | "planned";
}): Promise<void> {
  await api.post("/orders/control/remove", body);
}

// ── bulk-агрегат складов (кластеры, Excel) ──
export interface WarehousesAgg {
  date_from: string;
  date_to: string;
  as_of: string;
  generated_at: string;
  from_cache: boolean;
  selected_days: number;
  rows: {
    article: string;
    mp_code: "wb" | "ozon" | "ym";
    warehouse_name: string;
    qty: number;
    total: number;
    stock_mp: number;
  }[];
}

export async function getWarehousesAgg(params?: {
  date_from?: string;
  date_to?: string;
}): Promise<WarehousesAgg> {
  const { data } = await api.get<WarehousesAgg>("/orders/warehouses-agg", { params });
  return data;
}

// ── журнал ──
export async function listLog(params: {
  username?: string;
  action?: string;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<LogList> {
  const { data } = await api.get<LogList>("/orders/log", { params });
  return data;
}

// ── admin-импорт ──
export async function importStore(file: File): Promise<{ imported: Record<string, number> }> {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post<{ imported: Record<string, number> }>(
    "/orders/admin/import-store",
    fd,
  );
  return data;
}
